import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useIsAuthenticated } from "@azure/msal-react";
import { useMsalAuthentication, useMsal } from "@azure/msal-react";
import { InteractionType } from '@azure/msal-browser';
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { MotifProgressLoader } from '@ey-xd/motif-react'
import SideBar from '../components/SideBar/SideBar'
import Header from 'components/Header/Header';
import ReportGenerator from 'components/ReportGenerator/ReportGenerator';
import ReportLibrary from 'components/ReportLibrary/ReportLibrary';
import Clients from 'components/Clients/Clients';
import ErrorBoundary from 'common/components/ErrorBoundary/ErrorBoundary';
import PrivacyPolicyModal from 'components/PrivacyPolicyModal';
import { acceptPolicyAction, getPolicyAction, resetPolicyError } from '../redux/actions/PaansIntegration/paansIntegration';
import Toast from 'common/components/Toast/Toast';
import LandingPage from 'components/LandingPage/LandingPage';
import ApplicationProfile from 'components/ApplicationProfile/ApplicationProfile';
import UserManagement from 'components/UserManagement/UserManagement';
import './AppLayout.scss';

const AppLayout = () => {
    const sideCollapsed = useSelector((state: any) => state?.sideBar?.collapsed) || false;
    const { isPolicyAccepted, checkPolicyAccepted, regionCode, language, errorMessage: policyErrorMessage } = useSelector((state: any) => state?.paansIntegration);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isAuthenticated = useIsAuthenticated();
    const { login, result, error } = useMsalAuthentication(InteractionType.Redirect);
    const { instance, accounts, inProgress } = useMsal();
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const location = useLocation();

    const payload = {
        "region": regionCode,
        "language": language,
        "policyType": 6,
        "userId": accounts[0]?.username,
        "includeContent": true,
        "getContent": true
    };

    const onAcceptPolicy = () => {
        const { getContent, ...restPayload } = payload
        dispatch(acceptPolicyAction(restPayload));
    }

    // Check if tenant is available in sessionStorage
    useEffect(() => {
        const tenant = sessionStorage.getItem('tenant');
        const isLandingPage = location.pathname === '/';
        
        if (!tenant && !isLandingPage && isAuthenticated) {
            setShowSpinner(true);
            setTimeout(() => {
                navigate('/');
                setShowSpinner(false);
            }, 3000);
        }
    }, [location, isAuthenticated, navigate]);

    useEffect(() => {
        const fetchPolicyWithToken = async () => {
            if (checkPolicyAccepted === false && accounts && accounts.length > 0) {
                setShowSpinner(true);
                try {
                    const idTokenRequest = {
                        // 'email' scope ensures the backend sees an email-like claim on the token.
                        scopes: ["openid", "profile", "email"],
                        account: accounts[0],
                    };
                    const tokenResponse = await instance.acquireTokenSilent(idTokenRequest);
                    const bearerToken = tokenResponse.accessToken || tokenResponse.idToken;
                    await dispatch(getPolicyAction(payload, bearerToken));
                } catch (e) {
                    console.error(e);
                } finally {
                    setShowSpinner(false);
                }
            }
        };
        fetchPolicyWithToken();
    }, [checkPolicyAccepted, accounts, dispatch, instance]);

    if (!isAuthenticated) {
        const idTokenRequest = {
            // 'email' scope ensures the backend sees an email-like claim on the token.
            scopes: ["openid", "profile", "email"], // Scopes required for idToken
            account: accounts[0],
        };
        if (accounts[0]) {
            instance.acquireTokenSilent(idTokenRequest)
                .then((tokenResponse) => {
                    // Acquire token silent success
                    let idToken = tokenResponse.idToken; // Retrieve idToken
                    sessionStorage.setItem('idToken', idToken);
                    sessionStorage.setItem('user', accounts[0].name || '');
                    sessionStorage.setItem('user_mail', accounts[0]?.username || '');
                    // Dev-only sanity check: the backend derives identity from one of
                    // preferred_username | email | upn | unique_name. Warn if none present.
                    if (process.env.NODE_ENV === 'development') {
                        try {
                            const parts = (idToken || '').split('.');
                            if (parts.length >= 2) {
                                const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
                                const claims = JSON.parse(atob(b64));
                                const emailClaims = ['preferred_username', 'email', 'upn', 'unique_name'];
                                if (!emailClaims.some((c) => Boolean(claims?.[c]))) {
                                    // eslint-disable-next-line no-console
                                    console.warn('[Auth] Acquired token has no preferred_username | email | upn | unique_name claim. Backend identity checks will fail. Add the "email" scope and/or the "email"/"upn" optional claim in Azure AD App Registration → Token configuration.');
                                }
                            }
                        } catch (decodeErr) {
                            // eslint-disable-next-line no-console
                            console.warn('[Auth] Unable to decode ID token to verify identity claims.', decodeErr);
                        }
                    }
                })
                .catch((e) => {
                    console.error(e);
                });
        }

        const loadingStartUp = inProgress === 'startup';
        const loadingLogin = inProgress === 'login';
        const loadingRedirect = inProgress === 'handleRedirect';
        const loadingLogout = inProgress === 'logout';
        const inProgressNone = inProgress === 'none';
        return (
            <div>
                {loadingLogin && <p>Logging you in</p>}
                {loadingRedirect && <MotifProgressLoader show={true} className="fullscreen-progress-loader" />}
                {loadingLogout && <p>Logging you out</p>}
                {!loadingLogin && !loadingStartUp && !loadingRedirect && !loadingLogout && <p>You are not authorized to login</p>}
            </div>
        );
    }    
    
    const isLandingPage = location.pathname === '/';

    return (
        <div className='wrapper'>
            <ErrorBoundary>
                {!isLandingPage && <Header />}
            </ErrorBoundary>
            <ErrorBoundary>
                <div className="content">
                    {isAuthenticated && isPolicyAccepted === false && <PrivacyPolicyModal onAcceptPolicy={onAcceptPolicy}/>}
                    {!isLandingPage && <SideBar collapsed={sideCollapsed} />}
                    <div className="main">
                        <Routes>
                            <Route
                                path={'/'}
                                element={<LandingPage />}
                            />
                            <Route path={`reportGenerator/*`} element={<ReportGenerator />} />
                            <Route path={`reportLibrary/*`} element={<ReportLibrary />} />
                            <Route path={`clients/*`} element={<Clients />} />
                            <Route path={`applicationProfile/*`} element={<ApplicationProfile />} />
                            <Route path={`userManagement/*`} element={<UserManagement />} />
                        </Routes>
                    </div>
                </div>
                <MotifProgressLoader show={showSpinner} className="fullscreen-progress-loader" />
                <Toast
                    visible={Boolean(policyErrorMessage)}
                    setVisible={() => dispatch(resetPolicyError())}
                    children={
                        <div>
                            <div className={"toastHeader"}>{policyErrorMessage}</div>
                            <div className={"toastContent"}>Please refresh the page and review the privacy policy to proceed.</div>
                        </div>
                    }
                    variant="error"
                />
            </ErrorBoundary>
        </div>
    );
}

export default AppLayout;
